/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { memo } from 'react';
import { Chart } from '@scuf/charts';
import { useTranslation } from '@scuf/localization';
import descriptors from '../../../locales/en-US/descriptors';
import IChartWidgetProps from './IChartWidgetProps';
import '../../../scss/TooltipCommonTheme.scss';
import './TooltipChartWidgetCommonTheme.scss';
import { formatters } from '@hcecbp/i18n-utils';
import {
  formatTheValue,
  getChartTooltipInfo,
  shouldNotUpdate,
  symbolObjForBuilder
} from '../Utils/Utils';
import { IAny } from '../../DashboardKPIWidgets/IEnergyManagementProps';
import { EnergyUsageDataSourceType } from '../Enums';
const ChartWidgetPortfolio = memo((props: IChartWidgetProps) => {
  const [t] = useTranslation();
  const d = descriptors.energyManagement;
  const {
    graphData,
    unit,
    axisUnitType,
    height,
    legendBaseline,
    utilityGraphText,
    removePadding,
    graphType,
    hideHeader,
    tickIntervalEnable,
    showAllTooltips
  } = props;
  const {
    actual,
    previousPeriod,
    IsCompleted,
    YearIndex,
    MonthIndex,
    PeriodUpto,
    noDataActual,
    noDataPrevious,
    SiteWithDataCount,
    totalSiteCount
  } = graphData;

  const renderTooltip = (details: IAny) => {
    {
      const { x } = details;
      const period = PeriodUpto[x];
      const tooltipMonth = MonthIndex[x];
      const tooltipYear = YearIndex[x];
      const siteWithDataCount =
        SiteWithDataCount && typeof SiteWithDataCount === 'object'
          ? SiteWithDataCount[x]
          : SiteWithDataCount ?? null;
      const actualValueCheck =
        typeof actual[x] === 'object' ? (actual[x] as IAny) : actual[x];
      const actualValue =
        typeof actual[x] === 'object'
          ? (actualValueCheck['y'] as any)
          : actualValueCheck;
      const previousPeriodValue = previousPeriod[x];
      const partialDate = period;
      const isCompletedData = IsCompleted[x];

      if (details.label == 'Actual')
        return (
          <div className="tooltip-top">
            <div>
              <span className="tooltip-date emaasThemeColor">
                {tooltipMonth} {tooltipYear}
              </span>
            </div>
            <div className="tooltip-pane">
              <div className="tooltip-pane-item">
                <div className="tooltip-heading-container">
                  <span
                    className="dot"
                    style={{ backgroundColor: '#1272B2' }}
                  ></span>
                  <span className="tooltip-header-label emaasThemeColor">
                    {props.renderedFromCarbon
                      ? t(d.ACTUALEMISSIONS)
                      : t(d.ACTUAL)}
                    <label
                      className="italic-font"
                      style={{ fontStyle: 'italic' }}
                    >
                      {!isCompletedData ? t(d.PARTIAL_MONTH) : ''}
                    </label>
                  </span>
                </div>
                <div>
                  <span className="tooltip-header-value emaasThemeColor">
                    {getChartTooltipInfo(axisUnitType, actualValue, unit)}
                  </span>
                </div>
              </div>
              <div className="tooltip-pane-item">
                <div className="tooltip-heading-container">
                  <span
                    className="dot"
                    style={{ backgroundColor: '#606061' }}
                  ></span>
                  <span className="tooltip-header-label emaasThemeColor">
                    {props.renderedFromCarbon
                      ? t(d.PREVIOUSEMISSIONS)
                      : t(d.PREVIOUSPERIOD)}{' '}
                  </span>
                </div>
                <div>
                  <span className="tooltip-header-value emaasThemeColor">
                    {getChartTooltipInfo(
                      axisUnitType,
                      previousPeriodValue,
                      unit
                    )}
                  </span>
                </div>
              </div>
              {siteWithDataCount !== null &&
              totalSiteCount &&
              siteWithDataCount !== 0 &&
              siteWithDataCount < totalSiteCount &&
              !props.renderedFromCarbon &&
              showAllTooltips ? (
                <div className="tooltip-pane-item">
                  <p className="basic-site-with-data-count-msg">
                    {t(d.BASICSITEWITHDATACOUNTMESSAGE, {
                      siteWithDataCount,
                      totalSiteCount
                    })}
                  </p>
                </div>
              ) : (
                ''
              )}

              {props.renderedFromCarbon &&
              props.dataSourceType ===
                EnergyUsageDataSourceType.UTILITY_METER &&
              siteWithDataCount !== null &&
              totalSiteCount &&
              siteWithDataCount !== 0 &&
              siteWithDataCount < totalSiteCount ? (
                <div className="tooltip-pane-item">
                  <p className="basic-site-with-data-count-msg">
                    {t(d.CARBONUTILITYGRAPHTOOLTIP, {
                      siteWithDataCount,
                      totalSiteCount
                    })}
                  </p>
                </div>
              ) : (
                ''
              )}
            </div>
          </div>
        );
    }
  };
  const YAxisProps: IAny = {
    allowDecimals: true,
    gridLineColor: '#505050',
    gridLineWidth: 1,
    className: 'chart-widget-yaxis'
  };

  const getPreviousChart = () => {
    return graphType === 'lineGraph' ? (
      <Chart.Line
        name={d.PREVIOUSPERIOD.defaultMessage}
        data={previousPeriod}
        color="#606061"
      />
    ) : (
      <Chart.Column
        name={d.PREVIOUSPERIOD.defaultMessage}
        data={previousPeriod}
        color="#606061"
      />
    );
  };

  const getActualChart = () => {
    return graphType === 'lineGraph' ? (
      <Chart.Line
        name={d.ACTUAL.defaultMessage}
        data={actual}
        color="#1272B2"
      />
    ) : (
      <Chart.Column
        name={d.ACTUAL.defaultMessage}
        data={actual}
        color="#1272B2"
      />
    );
  };

  const getBaseLine = () => {
    return legendBaseline
      ? t(d.BASELINE)
      : utilityGraphText
      ? t(d.PREVIOUS)
      : t(d.PREVIOUSPERIOD);
  };

  if (tickIntervalEnable) {
    const previousHeighesVal = Math.max(...previousPeriod);
    const actualHeighesVal = Math.max(...actual.map((val: IAny) => val.y));
    const defference = (actualHeighesVal + previousHeighesVal) / 4;
    YAxisProps['tickInterval'] = Number(defference.toFixed());
  } else {
    YAxisProps['tickAmount'] = 4;
  }

  const currencyMapper = [
    'CRC',
    'SAR',
    'AED',
    'PLN',
    'MYR',
    'HUF',
    'USD',
    'AUD',
    'CAD',
    'CNY',
    'SGD',
    'MX$',
    'MEX$',
    'RM'
  ];

  return (
    <div className="emaas-chart-widget-container">
      {
        <div className="chart-widget-container">
          <Chart
            showLegend={false}
            spacingLeft={2}
            height={props.optimusSubscribed ? 120 : height ? height : 200}
          >
            {getPreviousChart()}
            {getActualChart()}
            <Chart.XAxis
              categories={MonthIndex}
              className="chart-widget-xaxis"
            />
            {axisUnitType === 'currency' && (
              <Chart.YAxis
                labels={{
                  formatter() {
                    const formatedValue = formatters.number.currency(unit)(
                      this.value
                    );
                    let updatedVal;
                    if (this.value == 0) {
                      let checkDKKChanges = formatters.number.currency(
                        unit || 'USD'
                      )(this.value);
                      // As "Kr." is the currency symbol for Danish Krone. the lib which we are using will give "DKK" so in the bellow condition if the currency in DKK will change it to "kr."
                      if (checkDKKChanges.slice(0, 3) === 'DKK') {
                        updatedVal = `kr. ${checkDKKChanges.slice(4)}`;
                      } else if (
                        currencyMapper.includes(checkDKKChanges.slice(0, 3))
                      ) {
                        updatedVal = `${
                          symbolObjForBuilder[checkDKKChanges.slice(0, 3)]
                        } ${checkDKKChanges.slice(4)}`;
                      } else {
                        updatedVal = checkDKKChanges;
                      }
                    } else {
                      updatedVal = formatTheValue(formatedValue);
                      if (currencyMapper.includes(updatedVal.slice(0, 3))) {
                        updatedVal = `${
                          symbolObjForBuilder[updatedVal.slice(0, 3)]
                        } ${updatedVal.slice(4)}`;
                      } else if (updatedVal.slice(0, 1) === '-') {
                        if (currencyMapper.includes(updatedVal.slice(1, 4))) {
                          updatedVal = `-${
                            symbolObjForBuilder[updatedVal.slice(1, 4)]
                          } ${updatedVal.slice(5)}`;
                        }
                      }
                    }

                    return updatedVal;
                  }
                }}
                {...YAxisProps}
              />
            )}
            {axisUnitType === 'consumption' && (
              <Chart.YAxis labelSuffix={`${''} ${unit}`} {...YAxisProps} />
            )}
            <Chart.Tooltip
              borderRadius={10}
              valueSuffix={unit}
              renderContent={renderTooltip}
              backgroundColor="white"
              shared={true}
            />
          </Chart>
        </div>
      }
      {hideHeader ? (
        ''
      ) : (
        <div
          className={`${removePadding ? 'legends removePadding' : 'legends'}`}
        >
          <div className="flex-container">
            <div className="legend-baseline">
              {props.renderedInHugUI
                ? t(d.PREVIOUSEMIS)
                : props.renderedFromCarbon
                ? t(d.PREVIOUSEMISSIONS)
                : getBaseLine()}
            </div>
            <div className="legend-actual-Border">
              <div className="legend-actual">
                {props.renderedInHugUI
                  ? t(d.ACTUALEMIS)
                  : props.renderedFromCarbon
                  ? t(d.ACTUALEMISSIONS)
                  : t(d.ACTUAL)}
              </div>
            </div>
            {noDataActual || noDataPrevious ? (
              <div className="legends-nodata">
                <div className="legends-nodata-icon"></div>
                <div className="legends-nodata-text">{t(d.NODATA)}</div>
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}, shouldNotUpdate);

export default ChartWidgetPortfolio;
